/* eslint-disable no-unused-vars */
import { ReactNode } from 'react'

import { IconButton } from '@mui/material'
import { Col, Span } from 'components'
import Masker from 'hooks/useMasker'
import { Controller } from 'react-hook-form'
import { COLORS, SPACING } from 'utils'

import { CustomInput, InputStyles, InputLabelCustom } from './styles'

interface InputProps extends InputStyles {
  control?: any
  errors?: any
  label?: string
  formValue?: string
  value?: string | number
  required?: boolean
  placeholder?: string
  inputIcon?: ReactNode
  onClickIcon?: (e?: any) => void
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  onKeyPress?: React.KeyboardEventHandler<HTMLDivElement>
  onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>
  endAdornment?: React.ReactNode
  onFocus?: () => void
  type?: string
  onChangeControl?: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void
  mask?: string[]
  name?: string
  disabled?: boolean
  maxLength?: number
  defaultValue?: string
  ref?: any
  min?: string | number
  onBlur?: (e: any) => void
  filter?: RegExp
  customError?: any
  multiline?: boolean
  count?: number
  customMask?: (_value: string) => string[]
  style?: React.CSSProperties
}

export const Input: React.FC<InputProps> = ({
  customError,
  min,
  control,
  label,
  errors,
  required,
  placeholder,
  disabled = false,
  type = 'text',
  marginTop,
  onChangeControl,
  value,
  inputIcon,
  onClickIcon,
  onChange,
  onKeyPress,
  onKeyDown,
  onBlur,
  marginBottom,
  marginLeft,
  marginRight,
  mask,
  formValue,
  maxLength = 1500,
  defaultValue,
  ref,
  filter,
  multiline,
  count,
  onFocus,
  customMask,
  style,
  ...props
}) => {
  const hasError = (errors && errors[formValue]) || customError
  const error = hasError && errors ? errors[formValue] : customError
  const formatLabel = label?.charAt(0).toUpperCase() + label?.slice(1)

  const withFilter = (value: string | number) => {
    if (!value) {
      return ''
    }

    if (customMask && value) {
      return Masker(String(value), customMask(String(value)))
    }

    if (mask) {
      value = Masker(String(value), mask)
      return value
    }

    if (filter) {
      return value?.toString().replace(filter, '')
    }
    return value
  }

  return (
    <Col
      marginBottom={marginBottom}
      marginLeft={marginLeft}
      marginRight={marginRight}
      marginTop={marginTop}
    >
      <InputLabelCustom hasError={hasError}>
        {formatLabel || ''}
      </InputLabelCustom>
      {control ? (
        <Controller
          name={formValue}
          control={control}
          defaultValue={defaultValue}
          render={({ field: { onChange, value }, fieldState: { error } }) => {
            return (
              <CustomInput
                {...props}
                multiline={multiline}
                disabled={disabled}
                onFocus={onFocus}
                type={type}
                marginBottom={hasError && SPACING.spacing5}
                value={withFilter(value)}
                onChange={(event) => {
                  onChange(event)
                  if (onChangeControl) {
                    onChangeControl(event)
                  }
                }}
                onKeyPress={onKeyPress}
                onKeyDown={onKeyDown}
                id={`${formValue ?? label}-${type}`}
                placeholder={placeholder}
                error={!!error}
                hasError={hasError}
                variant="standard"
                onBlur={onBlur}
                ref={ref}
                style={style}
                autoComplete="new-password"
                InputProps={{
                  endAdornment: (
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={onClickIcon}
                      style={{ margin: 0, padding: 0 }}
                    >
                      {inputIcon}
                    </IconButton>
                  ),
                  inputProps: { maxlength: maxLength, min: min }
                }}
              />
            )
          }}
          rules={{ required }}
        />
      ) : (
        <CustomInput
          {...props}
          multiline={multiline}
          defaultValue={defaultValue}
          disabled={disabled}
          type={type}
          marginBottom={hasError && SPACING.spacing5}
          value={withFilter(value)}
          onChange={onChange}
          onKeyPress={onKeyPress}
          placeholder={placeholder}
          id={`${formValue ?? label}-${type}`}
          hasError={hasError}
          variant="standard"
          onBlur={onBlur}
          ref={ref}
          style={style}
          name={formValue}
          autoComplete="new-password"
          InputProps={{
            endAdornment: (
              <IconButton
                aria-label="toggle password visibility"
                onClick={onClickIcon}
                style={{ margin: 0, padding: 0 }}
              >
                {inputIcon}
              </IconButton>
            ),
            inputProps: { maxlength: maxLength, min: min }
          }}
        />
      )}
      {count > 0 && (
        <Span
          textAlign="right"
          color={count == maxLength ? COLORS.error : COLORS.black}
          text={`${count}/${maxLength}`}
        />
      )}
      {hasError && (
        <Span text={error?.message || customError || 'Erro.'} color={COLORS.error} />
      )}
    </Col>
  )
}
